import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import translations from './translations';

// Language belongs to the organisation, not the user. A Swedish farm writes
// its checks in Swedish and its operators read Swedish — the interface should
// match the content around it.
//
// Only the strings we wrote are translated. Anything a customer typed — check
// items, machine names, defect descriptions — is shown as they wrote it.

export const LANGUAGES = [
  { code: 'en-GB', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'sv', label: 'Svenska' },
  { code: 'no', label: 'Norsk' },
  { code: 'da', label: 'Dansk' },
  { code: 'fi', label: 'Suomi' },
];

const FALLBACK = 'en-GB';

const LanguageContext = createContext({ code: FALLBACK });

// A lookup bound to one language code. Used by useTranslation below, and
// directly by the public passport verify page, which has no signed-in
// organisation to take a language from.
export function makeTranslator(code) {
  return (key) => {
    const table = translations[code];
    if (table && table[key]) return table[key];
    // An untranslated key falls back to English rather than showing the key.
    return translations[FALLBACK]?.[key] ?? key;
  };
}

// Best match for a browser language tag against the languages we support.
// 'sv-SE' -> 'sv', 'en-US' -> 'en-GB', anything unsupported -> English.
export function resolveLanguage(tag) {
  const raw = String(tag || '').trim();
  if (!raw) return FALLBACK;
  if (translations[raw]) return raw;
  let primary = raw.split('-')[0].toLowerCase();
  if (primary === 'en') return FALLBACK;
  // Norwegian browsers report nb (Bokmal) or nn (Nynorsk); our table is 'no'.
  if (primary === 'nb' || primary === 'nn') primary = 'no';
  const supported = LANGUAGES.map((l) => l.code);
  const hit = supported.find((c) => c.split('-')[0].toLowerCase() === primary);
  return hit && translations[hit] ? hit : FALLBACK;
}

export function LanguageProvider({ children }) {
  const { org } = useAuth();

  // Signup stores a bare code like 'sv'; the tables are keyed 'sv' too, but
  // English is regional, so map the plain code across.
  const code = useMemo(() => {
    const raw = org?.preferredLanguage;
    if (!raw) return FALLBACK;
    if (raw === 'en') return FALLBACK;
    return translations[raw] ? raw : FALLBACK;
  }, [org]);

  return (
    <LanguageContext.Provider value={{ code }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const { code } = useContext(LanguageContext);

  const t = makeTranslator(code);

  return { t, language: code };
}
